// MES (Manufacturing Execution System) Types

// =============================================================================
// Enums / Union Types
// =============================================================================

export type AssemblyType = 'pcba' | 'module' | 'product';
export type MOStatus = 'draft' | 'planned' | 'released' | 'in_progress' | 'completed' | 'cancelled';
export type WOStatus = 'pending' | 'ready' | 'in_progress' | 'paused' | 'completed' | 'on_hold';
export type Priority = 'low' | 'normal' | 'high' | 'urgent';
export type DefectSeverity = 'minor' | 'major' | 'critical';
export type DefectStatus = 'open' | 'investigating' | 'resolved' | 'closed';
export type CheckpointType = 'visual' | 'measurement' | 'functional' | 'ict';
export type OperatorRole = 'lead' | 'operator' | 'qc';

// =============================================================================
// Assembly Type Configuration (for React Flow nodes)
// =============================================================================

export const ASSEMBLY_TYPES: AssemblyType[] = ['pcba', 'module', 'product'];

export const ASSEMBLY_TYPE_LABELS: Record<AssemblyType, string> = {
  pcba: 'PCB Assembly',
  module: 'Module Assembly',
  product: 'Product Assembly',
};

export const ASSEMBLY_TYPE_LABELS_ZH: Record<AssemblyType, string> = {
  pcba: 'PCB組裝',
  module: '模組組裝',
  product: '成品組裝',
};

// Color scheme for React Flow nodes (based on plan: blue, orange, green)
export const ASSEMBLY_TYPE_COLORS: Record<AssemblyType, {
  bg: string;
  border: string;
  text: string;
  badge: string;
}> = {
  pcba: {
    bg: 'bg-blue-50 dark:bg-blue-950',
    border: 'border-blue-500',
    text: 'text-blue-700 dark:text-blue-300',
    badge: 'bg-blue-500 text-white',
  },
  module: {
    bg: 'bg-orange-50 dark:bg-orange-950',
    border: 'border-orange-500',
    text: 'text-orange-700 dark:text-orange-300',
    badge: 'bg-orange-500 text-white',
  },
  product: {
    bg: 'bg-green-50 dark:bg-green-950',
    border: 'border-green-500',
    text: 'text-green-700 dark:text-green-300',
    badge: 'bg-green-500 text-white',
  },
};

// =============================================================================
// Status Labels & Colors
// =============================================================================

export const MO_STATUS_LABELS: Record<MOStatus, string> = {
  draft: 'Draft',
  planned: 'Planned',
  released: 'Released',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const MO_STATUS_LABELS_ZH: Record<MOStatus, string> = {
  draft: '草稿',
  planned: '已計劃',
  released: '已發布',
  in_progress: '進行中',
  completed: '已完成',
  cancelled: '已取消',
};

export const MO_STATUS_COLORS: Record<MOStatus, string> = {
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  planned: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  released: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export const WO_STATUS_LABELS: Record<WOStatus, string> = {
  pending: 'Pending',
  ready: 'Ready',
  in_progress: 'In Progress',
  paused: 'Paused',
  completed: 'Completed',
  on_hold: 'On Hold',
};

export const WO_STATUS_LABELS_ZH: Record<WOStatus, string> = {
  pending: '待處理',
  ready: '準備就緒',
  in_progress: '進行中',
  paused: '暫停',
  completed: '已完成',
  on_hold: '暫緩',
};

export const WO_STATUS_COLORS: Record<WOStatus, string> = {
  pending: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  ready: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  paused: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  on_hold: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export const PRIORITY_LABELS: Record<Priority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

export const PRIORITY_LABELS_ZH: Record<Priority, string> = {
  low: '低',
  normal: '一般',
  high: '高',
  urgent: '緊急',
};

export const PRIORITY_COLORS: Record<Priority, string> = {
  low: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  normal: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  urgent: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export const DEFECT_SEVERITY_LABELS: Record<DefectSeverity, string> = {
  minor: 'Minor',
  major: 'Major',
  critical: 'Critical',
};

export const DEFECT_SEVERITY_COLORS: Record<DefectSeverity, string> = {
  minor: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  major: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export const DEFECT_STATUS_LABELS: Record<DefectStatus, string> = {
  open: 'Open',
  investigating: 'Investigating',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const DEFECT_STATUS_COLORS: Record<DefectStatus, string> = {
  open: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  investigating: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  resolved: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  closed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
};

export const CHECKPOINT_TYPE_LABELS: Record<CheckpointType, string> = {
  visual: 'Visual',
  measurement: 'Measurement',
  functional: 'Functional',
  ict: 'ICT',
};

export const CHECKPOINT_TYPE_LABELS_ZH: Record<CheckpointType, string> = {
  visual: '目視',
  measurement: '量測',
  functional: '功能',
  ict: 'ICT測試',
};

export const OPERATOR_ROLE_LABELS: Record<OperatorRole, string> = {
  lead: 'Lead',
  operator: 'Operator',
  qc: 'QC',
};

// =============================================================================
// Core Interfaces
// =============================================================================

export interface IEmbeddedBOMItem {
  line_no: number;
  part_code: string;
  description: string;
  qty_per: number;
  unit: string;
  ref_designator?: string;
  is_assembly: boolean;
}

export interface IOperatorAssignment {
  employee_id: string;
  employee_name: string;
  role: OperatorRole;
  assigned_at: string;
  completed_at?: string;
}

export interface IManufacturingOrder {
  id: string;
  mo_number: string;
  product_id: string;
  product_revision: string;
  product_name: string;
  product_code: string;
  planned_qty: number;
  completed_qty: number;
  scrapped_qty: number;
  status: MOStatus;
  priority: Priority;
  scheduled_start?: string;
  scheduled_end?: string;
  actual_start?: string;
  actual_end?: string;
  notes?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface IWorkOrder {
  id: string;
  mo_id: string;
  wo_number: string;
  assembly_type: AssemblyType;
  bom_product_id: string;
  bom_revision: string;
  bom_product_name: string;
  bom_product_code: string;
  parent_wo_id?: string;
  level: number;
  sequence: number;
  bom_items: IEmbeddedBOMItem[];
  planned_qty: number;
  completed_qty: number;
  scrapped_qty: number;
  status: WOStatus;
  work_instructions?: string;
  operators?: IOperatorAssignment[];
  planned_start?: string;
  planned_end?: string;
  actual_start?: string;
  actual_end?: string;
  cycle_time_minutes: number;
  actual_cycle_time_minutes: number;
  lot_number?: string;
  serial_numbers?: string[];
  requires_inspection: boolean;
  inspection_passed?: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface IDefect {
  id: string;
  wo_id: string;
  defect_code: string;
  description: string;
  severity: DefectSeverity;
  quantity: number;
  root_cause?: string;
  corrective_action?: string;
  status: DefectStatus;
  discovered_by: string;
  discovered_at: string;
  resolved_by?: string;
  resolved_at?: string;
}

export interface IQualityCheckpoint {
  id: string;
  wo_id: string;
  checkpoint_name: string;
  checkpoint_type: CheckpointType;
  specification: string;
  actual_value: string;
  passed: boolean;
  notes?: string;
  inspector_id: string;
  inspector_name: string;
  inspected_at: string;
}

export interface IMaterialConsumption {
  id: string;
  wo_id: string;
  part_code: string;
  part_description: string;
  planned_qty: number;
  consumed_qty: number;
  scrapped_qty: number;
  lot_numbers?: string[];
  consumed_by: string;
  consumed_at: string;
}

export interface ITimeLog {
  id: string;
  wo_id: string;
  employee_id: string;
  activity: string;
  start_time: string;
  end_time?: string;
  notes?: string;
}

// =============================================================================
// Request Types
// =============================================================================

export interface ICreateMORequest {
  product_id: string;
  product_revision: string;
  planned_qty: number;
  priority?: Priority;
  scheduled_start?: string;
  scheduled_end?: string;
  notes?: string;
}

export interface IUpdateMORequest {
  priority?: Priority;
  scheduled_start?: string;
  scheduled_end?: string;
  notes?: string;
}

export interface IUpdateMOStatusRequest {
  status: MOStatus;
}

export interface IUpdateWORequest {
  work_instructions?: string;
  planned_start?: string;
  planned_end?: string;
  lot_number?: string;
  cycle_time_minutes?: number;
}

export interface IStartWORequest {
  operator_id: string;
}

export interface ICompleteWORequest {
  completed_qty: number;
  scrapped_qty: number;
}

export interface IAssignOperatorRequest {
  employee_id: string;
  employee_name: string;
  role: OperatorRole;
}

export interface ICreateDefectRequest {
  defect_code: string;
  description: string;
  severity: DefectSeverity;
  quantity: number;
}

export interface IUpdateDefectRequest {
  root_cause?: string;
  corrective_action?: string;
  status?: DefectStatus;
}

export interface ICreateCheckpointRequest {
  checkpoint_name: string;
  checkpoint_type: CheckpointType;
  specification: string;
  actual_value: string;
  passed: boolean;
  notes?: string;
  inspector_id: string;
  inspector_name: string;
}

export interface IRecordMaterialRequest {
  part_code: string;
  part_description: string;
  planned_qty: number;
  consumed_qty: number;
  scrapped_qty?: number;
  lot_numbers?: string[];
}

// =============================================================================
// Filter Types
// =============================================================================

export interface IMOFilter {
  status?: MOStatus;
  priority?: Priority;
  product_id?: string;
}

// =============================================================================
// React Flow Node Types
// =============================================================================

export interface IWONodeData {
  workOrder: IWorkOrder;
  onNodeClick?: (woId: string) => void;
}

// React Flow edge for work order hierarchy
export interface IWOEdge {
  id: string;
  source: string;
  target: string;
  type?: string;
  animated?: boolean;
}

// =============================================================================
// Dashboard / Summary Types
// =============================================================================

export interface IMOSummary {
  total_mos: number;
  by_status: Record<MOStatus, number>;
  by_priority: Record<Priority, number>;
}

export interface IWOSummary {
  total_wos: number;
  by_status: Record<WOStatus, number>;
  by_assembly_type: Record<AssemblyType, number>;
  completion_rate: number;
}

export interface IQualitySummary {
  total_checkpoints: number;
  passed: number;
  failed: number;
  pass_rate: number;
  defects_open: number;
  defects_resolved: number;
}

export interface IMESStats {
  mo_summary: IMOSummary;
  wo_summary: IWOSummary;
  quality_summary: IQualitySummary;
}
